import React from 'react';
import {Body, Headline} from '@lightricks/react-design-system';
import translate from '@/utils/translate';
import type {CampaignProps} from '@/types/campaign';
import getNetworkDisplayName from '../utils/networkDisplayName';
import styles from './LegacyDeliverablesSection.module.scss';

const DELIVERABLE_KIND_LABEL: Record<string, string> = {
  twitter_post: 'Twitter Post',
  pinterest_post: 'Pinterest Post',
  amazon_post: 'Amazon Post',
  blog_post: 'Blog Post',
  facebook_post: 'Facebook Post',
  reddit_post: 'Reddit Post',
  instagram_story_post: 'Instagram Story Post',
  review_post: 'Review Post',
  twitch_post: 'Twitch Post',
  linkedin_post: 'LinkedIn Post',
  instagram_reel_post: 'Instagram Reel Post',
  tiktok_post: 'TikTok Post',
  snapchat_post: 'Snapchat Post',
  instagram_post: 'Instagram Post',
  youtube_post: 'YouTube Post',
};

// Kinds covered by the legacy boolean fields — skip them from campaign.deliverables
// to avoid double-counting.
const LEGACY_FIELD_KINDS = new Set([
  'instagram_post',
  'instagram_reel_post',
  'instagram_story_post',
]);

interface DeliverableEntry {
  count: number;
  label: string;
}

function buildDeliverables(campaign: CampaignProps): DeliverableEntry[] {
  const network = getNetworkDisplayName(campaign.network);
  const entries: DeliverableEntry[] = [];

  if (campaign.photoUploadRequired && campaign.photosToUpload) {
    const label = campaign.network === 'pinterest' ? 'Pin' : 'Photo';
    entries.push({count: campaign.photosToUpload, label});
  }
  if (campaign.blogUploadRequired && campaign.blogsToUpload) {
    entries.push({count: campaign.blogsToUpload, label: 'Blog'});
  }
  if (campaign.videoUploadRequired && campaign.videosToUpload) {
    entries.push({count: campaign.videosToUpload, label: 'Video'});
  }
  if (campaign.mixedMediaUploadRequired && campaign.mixedMediaToUpload) {
    entries.push({count: campaign.mixedMediaToUpload, label: 'Story Upload'});
  }
  if (campaign.photoPostRequired && campaign.requiredFeedPostsCount) {
    entries.push({
      count: campaign.requiredFeedPostsCount,
      label: `${network} Post`,
    });
  }
  if (campaign.reelsPostRequired && campaign.requiredReelsPostsCount) {
    entries.push({
      count: campaign.requiredReelsPostsCount,
      label: `${network} Reels Post`,
    });
  }
  if (campaign.storyPostRequired && campaign.requiredStoryPostsCount) {
    entries.push({
      count: campaign.requiredStoryPostsCount,
      label: `${network} Story Post`,
    });
  }

  if (Array.isArray(campaign.deliverables)) {
    campaign.deliverables
      .filter((d) => !LEGACY_FIELD_KINDS.has(d.kind) && d.quantity)
      .forEach((d) => {
        entries.push({
          count: d.quantity,
          label: DELIVERABLE_KIND_LABEL[d.kind] ?? d.kind,
        });
      });
  }

  return entries;
}

interface LegacyDeliverablesSectionProps {
  campaign: CampaignProps;
}

function LegacyDeliverablesSection({campaign}: LegacyDeliverablesSectionProps) {
  const entries = buildDeliverables(campaign);
  if (!entries.length) {
    return null;
  }

  return (
    <div className={styles.container}>
      <Headline size="xs" className={styles.title}>
        {translate('What deliverables am I producing?')}
      </Headline>
      <div className={styles.items}>
        {entries.map(({count, label}) => (
          <div key={label} className={styles.entry}>
            <Headline size="sm">{count}</Headline>
            <Body size="md">{label}</Body>
          </div>
        ))}
      </div>
    </div>
  );
}

export default LegacyDeliverablesSection;
